import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import { createBrowserHistory } from 'history';
import { reducer as reduxFormReducer } from 'redux-form';

import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { dashboardApi } from 'dashboard/redux/api/api';
import accountActionsReducer from 'dashboard/redux/reducers/account-actions';
import accountStatusesReducer from 'dashboard/redux/reducers/account-statuses';
import accountTypesReducer from 'dashboard/redux/reducers/account-types';
import apiLogReducer from 'dashboard/redux/reducers/api-log';
import blueprintsReducer from 'dashboard/redux/reducers/blueprints';
import caseAnalyticsReducer from 'dashboard/redux/reducers/case-analytics';
import caseQueriesReducer from 'dashboard/redux/reducers/case-queries';
import caseTemplateReducer from 'dashboard/redux/reducers/case-template';
import caseTemplatesReducer from 'dashboard/redux/reducers/case-templates';
import casesReducer from 'dashboard/redux/reducers/cases';
import complianceRulesReducer from 'dashboard/redux/reducers/compliance-rules';
import currentEnvironmentReducer from 'dashboard/redux/reducers/current-environment';
import currentInvoiceReducer from 'dashboard/redux/reducers/current-invoice';
import currentUserReducer from 'dashboard/redux/reducers/current-user';
import currentUserSessionReducer from 'dashboard/redux/reducers/current-user-session';
import databaseCoverageReducer from 'dashboard/redux/reducers/database-coverage';
import documentCoverageReducer from 'dashboard/redux/reducers/document-coverage';
import documentViewerReducer from 'dashboard/redux/reducers/document-viewer-base';
import documentViewerDocumentGenericReducer from 'dashboard/redux/reducers/document-viewer-document-generic';
import editorReducer from 'dashboard/redux/reducers/editor';
import eventReducer from 'dashboard/redux/reducers/event';
import eventsReducer from 'dashboard/redux/reducers/events';
import exporterReducer from 'dashboard/redux/reducers/exporters';
import externalIntegrationReducer from 'dashboard/redux/reducers/external-integrations';
import featureFlagsReducer from 'dashboard/redux/reducers/feature-flags';
import governmentIdCoverageReducer from 'dashboard/redux/reducers/government-id-coverage';
import graphReducer from 'dashboard/redux/reducers/graph';
import graphDatumTypeReducer from 'dashboard/redux/reducers/graph-datum-type';
import graphQueryReducer from 'dashboard/redux/reducers/graph-query';
import graphQueryTemplateReducer from 'dashboard/redux/reducers/graph-query-template';
import graphQueryTemplatesReducer from 'dashboard/redux/reducers/graph-query-templates';
import graphSnapshotReducer from 'dashboard/redux/reducers/graph-snapshots';
import importerReducer from 'dashboard/redux/reducers/importer';
import importerRunReducer from 'dashboard/redux/reducers/importer-run';
import importerRunsReducer from 'dashboard/redux/reducers/importer-runs';
import importersReducer from 'dashboard/redux/reducers/importers';
import inquiriesReducer from 'dashboard/redux/reducers/inquiries';
import inquiryReducer from 'dashboard/redux/reducers/inquiry';
import invoiceReducer from 'dashboard/redux/reducers/invoice';
import invoicesReducer from 'dashboard/redux/reducers/invoices';
import listReducer from 'dashboard/redux/reducers/list';
import listsReducer from 'dashboard/redux/reducers/lists';
import messagesReducer from 'dashboard/redux/reducers/messages';
import metricsInquiryReducer from 'dashboard/redux/reducers/metrics-inquiry';
import metricsReportsReducer from 'dashboard/redux/reducers/metrics-reports';
import metricsTransactionReducer from 'dashboard/redux/reducers/metrics-transaction';
import onboardingFlowReducer from 'dashboard/redux/reducers/onboarding-flow';
import organizationReducer from 'dashboard/redux/reducers/organization';
import passwordResetReducer from 'dashboard/redux/reducers/password-reset';
import reportReducer from 'dashboard/redux/reducers/report';
import reportRunsReducer from 'dashboard/redux/reducers/report-runs';
import reportSnapshotReducer from 'dashboard/redux/reducers/report-snapshot';
import reportTemplateReducer from 'dashboard/redux/reducers/report-template';
import reportTemplateVersionReducer from 'dashboard/redux/reducers/report-template-version';
import reportTemplatesReducer from 'dashboard/redux/reducers/report-templates';
import reportsReducer from 'dashboard/redux/reducers/reports';
import roleReducer from 'dashboard/redux/reducers/role';
import rolesReducer from 'dashboard/redux/reducers/roles';
import s3Reducer from 'dashboard/redux/reducers/s3';
import tagsReducer from 'dashboard/redux/reducers/tags';
import teamReducer from 'dashboard/redux/reducers/team';
import teamsReducer from 'dashboard/redux/reducers/teams';
import transactionStatusesReducer from 'dashboard/redux/reducers/transaction-statuses';
import transactionTypesReducer from 'dashboard/redux/reducers/transaction-types';
import transactionsReducer from 'dashboard/redux/reducers/transactions';
import userReducer from 'dashboard/redux/reducers/user';
import userAuditLogReducer from 'dashboard/redux/reducers/user-audit-log';
import userAuditLogsReducer from 'dashboard/redux/reducers/user-audit-logs';
import userAuthenticationReducer from 'dashboard/redux/reducers/user-authentication';
import userSessionsReducer from 'dashboard/redux/reducers/user-sessions';
import verificationReducer from 'dashboard/redux/reducers/verification';
import verificationTemplatesReducer from 'dashboard/redux/reducers/verification-templates';
import verificationsReducer from 'dashboard/redux/reducers/verifications';
import workflowReducer from 'dashboard/redux/reducers/workflow';
import workflowRunReducer from 'dashboard/redux/reducers/workflow-run';
import workflowRunsReducer from 'dashboard/redux/reducers/workflow-runs';
import workflowSchemaReducer from 'dashboard/redux/reducers/workflow-schema';
import workflowVersionsReducer from 'dashboard/redux/reducers/workflow-versions';
import workflowsReducer from 'dashboard/redux/reducers/workflows';
import { accountReducer } from 'dashboard/redux/slices/account';
import { accountActionReducer } from 'dashboard/redux/slices/account-action';
import { accountTypeReducer } from 'dashboard/redux/slices/account-type';
import { accountsReducer } from 'dashboard/redux/slices/accounts';
import { analyticsQueryTemplatesReducer } from 'dashboard/redux/slices/analytics-query-templates';
import { apiKeyReducer } from 'dashboard/redux/slices/api-key';
import { apiKeysReducer } from 'dashboard/redux/slices/api-keys';
import { apiLogsReducer } from 'dashboard/redux/slices/api-logs';
import { caseReducer } from 'dashboard/redux/slices/case';
import { caseAssignmentPoliciesReducer } from 'dashboard/redux/slices/case-assignment-policies';
import { caseAssignmentPolicyReducer } from 'dashboard/redux/slices/case-assignment-policy';
import { caseQueueReducer } from 'dashboard/redux/slices/case-queue';
import { caseQueuesReducer } from 'dashboard/redux/slices/case-queues';
import { externalIntegrationCallReducer } from 'dashboard/redux/slices/external-integration-call';
import { inquiryInspectReducer } from 'dashboard/redux/slices/inquiry-inspect';
import { inquiryPreviewReducer } from 'dashboard/redux/slices/inquiry-preview';
import { inquiryRunReducer } from 'dashboard/redux/slices/inquiry-run';
import {
  formReducer as inquiryTemplateFormReducer,
  inquiryTemplateReducer,
} from 'dashboard/redux/slices/inquiry-template';
import { inquiryTemplateModulesReducer } from 'dashboard/redux/slices/inquiry-template-modules';
import { inquiryTemplatesReducer } from 'dashboard/redux/slices/inquiry-templates';
import { InsightQueriesReducer } from 'dashboard/redux/slices/insight-queries';
import { metricsApiLogsReducer } from 'dashboard/redux/slices/metrics-api-logs';
import { metricsVerificationReducer } from 'dashboard/redux/slices/metrics-verification';
import { navbarReducer } from 'dashboard/redux/slices/navbar';
import { objectSchemasReducer } from 'dashboard/redux/slices/object-schemas';
import { SARReducer } from 'dashboard/redux/slices/sar';
import { SARConfigurationReducer } from 'dashboard/redux/slices/sar-configuration';
import { SARsReducer } from 'dashboard/redux/slices/sars';
import { tableViewsReducer } from 'dashboard/redux/slices/table-views';
import { themeSetReducer } from 'dashboard/redux/slices/theme-set';
import { themeSetsReducer } from 'dashboard/redux/slices/theme-sets';
import { transactionTypeReducer } from 'dashboard/redux/slices/transaction-type';
import { translationsReducer } from 'dashboard/redux/slices/translations';
import { userNotificationsReducer } from 'dashboard/redux/slices/user-notifications';
import { verificationTemplateReducer } from 'dashboard/redux/slices/verification-template';
import { webhookReducer } from 'dashboard/redux/slices/webhook';
import { webhookEventReducer } from 'dashboard/redux/slices/webhook-event';
import { webhookEventsReducer } from 'dashboard/redux/slices/webhook-events';
import { webhooksReducer } from 'dashboard/redux/slices/webhooks';
import { workflowBlockReducer } from 'dashboard/redux/slices/workflow-block';
import { workflowEditorReducer } from 'dashboard/redux/slices/workflow-editor';
import withResetState from 'lib/redux/decorators/withResetState';

export const createRootReducer = () =>
  combineReducers({
    [dashboardApi.reducerPath]: dashboardApi.reducer,
    account: accountReducer,
    accounts: accountsReducer,
    accountAction: accountActionReducer,
    accountActions: accountActionsReducer,
    accountTypes: accountTypesReducer,
    accountType: accountTypeReducer,
    accountStatuses: accountStatusesReducer,
    analyticsQueryTemplates: analyticsQueryTemplatesReducer,
    apiKey: apiKeyReducer,
    apiKeys: apiKeysReducer,
    apiLog: apiLogReducer,
    apiLogs: apiLogsReducer,
    blueprints: blueprintsReducer,
    case: caseReducer,
    caseAnalytics: caseAnalyticsReducer,
    caseAssignmentPolicies: caseAssignmentPoliciesReducer,
    caseAssignmentPolicy: caseAssignmentPolicyReducer,
    caseQueue: caseQueueReducer,
    caseQueues: caseQueuesReducer,
    caseTemplate: caseTemplateReducer,
    caseTemplates: caseTemplatesReducer,
    caseQueries: caseQueriesReducer,
    cases: casesReducer,
    complianceRules: complianceRulesReducer,
    currentEnvironment: currentEnvironmentReducer,
    currentUser: currentUserReducer,
    currentUserSession: currentUserSessionReducer,
    insightQueries: InsightQueriesReducer,
    databaseCoverage: databaseCoverageReducer,
    documentCoverage: documentCoverageReducer,
    documentViewer: combineReducers({
      base: documentViewerReducer,
      documentGeneric: documentViewerDocumentGenericReducer,
    }),
    editor: editorReducer,
    event: eventReducer,
    events: eventsReducer,
    exporters: exporterReducer,
    externalIntegrations: externalIntegrationReducer,
    externalIntegrationCall: externalIntegrationCallReducer,
    featureFlags: featureFlagsReducer,
    form: reduxFormReducer.plugin({
      ...inquiryTemplateFormReducer,
    }),
    governmentIdCoverage: governmentIdCoverageReducer,
    graph: graphReducer,
    graphDatumType: graphDatumTypeReducer,
    graphQuery: graphQueryReducer,
    graphQueryTemplate: graphQueryTemplateReducer,
    graphQueryTemplates: graphQueryTemplatesReducer,
    graphSnapshots: graphSnapshotReducer,
    importer: importerReducer,
    importers: importersReducer,
    importerRuns: importerRunsReducer,
    importerRun: importerRunReducer,
    inquiries: inquiriesReducer,
    inquiry: inquiryReducer,
    inquiryRun: inquiryRunReducer,
    inquiryTemplate: inquiryTemplateReducer,
    inquiryTemplates: inquiryTemplatesReducer,
    inquiryTemplateModules: inquiryTemplateModulesReducer,
    inquiryPreview: inquiryPreviewReducer,
    inquiryInspect: inquiryInspectReducer,
    currentInvoice: currentInvoiceReducer,
    invoice: invoiceReducer,
    invoices: invoicesReducer,
    list: listReducer,
    lists: listsReducer,
    messages: messagesReducer,
    metricsApiLogs: metricsApiLogsReducer,
    metricsInquiry: metricsInquiryReducer,
    metricsTransaction: metricsTransactionReducer,
    metricsVerification: metricsVerificationReducer,
    metricsReports: metricsReportsReducer,
    navbar: navbarReducer,
    objectSchemas: objectSchemasReducer,
    onboardingFlow: onboardingFlowReducer,
    organization: organizationReducer,
    passwordReset: passwordResetReducer,
    report: reportReducer,
    reports: reportsReducer,
    reportRuns: reportRunsReducer,
    reportSnapshot: reportSnapshotReducer,
    reportTemplate: reportTemplateReducer,
    reportTemplates: reportTemplatesReducer,
    reportTemplateVersion: reportTemplateVersionReducer,
    role: roleReducer,
    roles: rolesReducer,
    s3: s3Reducer,
    sars: SARsReducer,
    sarConfiguration: SARConfigurationReducer,
    sar: SARReducer,
    tableViews: tableViewsReducer,
    tags: tagsReducer,
    team: teamReducer,
    teams: teamsReducer,
    themeSet: themeSetReducer,
    themeSets: themeSetsReducer,
    transactions: transactionsReducer,
    transactionStatuses: transactionStatusesReducer,
    transactionType: transactionTypeReducer,
    transactionTypes: transactionTypesReducer,
    translations: translationsReducer,
    user: userReducer,
    userAuditLog: userAuditLogReducer,
    userAuditLogs: userAuditLogsReducer,
    userAuthentication: userAuthenticationReducer,
    userNotifications: userNotificationsReducer,
    userSessions: userSessionsReducer,
    verification: verificationReducer,
    verificationTemplates: verificationTemplatesReducer,
    verificationTemplate: verificationTemplateReducer,
    verifications: verificationsReducer,
    webhook: webhookReducer,
    webhooks: webhooksReducer,
    webhookEvent: webhookEventReducer,
    webhookEvents: webhookEventsReducer,
    workflow: workflowReducer,
    workflows: workflowsReducer,
    workflowBlock: workflowBlockReducer,
    workflowEditor: workflowEditorReducer,
    workflowRuns: workflowRunsReducer,
    workflowRun: workflowRunReducer,
    workflowVersions: workflowVersionsReducer,
    workflowSchema: workflowSchemaReducer,
  });

const rootReducer = withResetState(createRootReducer());

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    /**
     * We should enable `immutableCheck` once we refactor most of our redux code
     * to use @reduxjs/toolkit. We encounter a ton of errors in Storybook if we
     * enable this currently.
     * - Joseph L.
     */
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: {
        /**
         * Omits the serializable check for the following actions and paths. Serializable check is turned off in production,
         * so this change only affects local dev.
         *
         * FLE usages are omitted because the large amounts of stored data are slow to check for serializability.
         * Workflow usages are omitted because the large nested structures of stored data are slow to check for serializability.
         * RTK Query rejected actions are omitted because our app raises Error objects which are not serializable.
         */
        ignoredActions: ['dashboardApi/executeQuery/rejected'],
        ignoreState: true,
        ignoredPaths: [
          `form.${INQUIRY_TEMPLATE_EDITOR_FORM_NAME}`,
          'inquiryTemplate',
          /dashboardApi.queries.+\.error/,
          // This is for workflowVersionErrors -- there is something wrong with how this middleware determines
          // paths to match against.
          'payload.errors',
        ],
      },
    }).concat([dashboardApi.middleware]),
});

setupListeners(store.dispatch);

export default store;

export const history = createBrowserHistory();

export type RootState = ReturnType<typeof rootReducer>;
